import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Plus, Save, Trash2, X, Loader2, Download, Send, MessageCircle, FileText, Users,
  Plane, Building2, Sun, ListChecks, Sparkles, ShieldCheck, IndianRupee, Copy, Link2, Check,
  Eye, EyeOff, MapPin, Calendar, Search, Wand2, ScrollText, Mail, ChevronDown, Clock, RotateCw,
} from 'lucide-react';
import { API, adminAuthHeader, resolveImg } from '../api';
import ImageUploader from './ImageUploader';
import VoucherPreviewModal from './VoucherPreviewModal';
import AdminSearch from './AdminSearch';
import { buildItineraryShareMessage } from '../utils/shareMessages';
import { fmtDate } from '../utils/dateFormat';

const emptyCustomer = { name: '', phone: '', email: '', city: '', state: '' };

const TABS = [
  { key: 'overview',   label: 'Overview',   icon: Sparkles },
  { key: 'flights',    label: 'Flights',    icon: Plane },
  { key: 'hotels',     label: 'Hotels',     icon: Building2 },
  { key: 'days',       label: 'Day-wise Plan', icon: Sun },
  { key: 'inclusions', label: 'Inclusions / Exclusions / Must-Have', icon: ListChecks },
  { key: 'payment',    label: 'Payment',    icon: IndianRupee },
  { key: 'terms',      label: 'Terms',      icon: ShieldCheck },
];

const VISIBILITY_ITEMS = [
  { key: 'show_greeting',   label: 'Greeting' },
  { key: 'show_overview',   label: 'Trip Overview' },
  { key: 'show_flights',    label: 'Flights' },
  { key: 'show_hotels',     label: 'Hotels' },
  { key: 'show_days',       label: 'Day-wise Itinerary' },
  { key: 'show_inclusions', label: 'Inclusions' },
  { key: 'show_exclusions', label: 'Exclusions' },
  { key: 'show_must_have',  label: 'Must-Have list' },
  { key: 'show_things_to_note', label: 'Things to Note' },
  { key: 'show_payment',    label: 'Payment Details' },
  { key: 'show_terms',      label: 'Terms & Conditions' },
];

const MEALS = ['Breakfast', 'Lunch', 'Dinner'];

const newDay = (n) => ({ day_number: n, title: '', date: '', images: [], description: '', meals_included: [], activities: [] });
const newFlight = () => ({ label: 'Onward', image: '', notes: '' });
const newHotel = () => ({ name: '', city: '', check_in: '', check_out: '', nights: 1, room_type: '', meal_plan: 'CP', images: [], booking_ref: '', map_link: '', early_late_options: '' });

const emptyForm = () => ({
  trip_title: '',
  customer: { ...emptyCustomer },
  destinations: [],
  start_date: '',
  end_date: '',
  pax_adults: 2,
  pax_children: 0,
  greeting: '',
  cover_image: '',
  flights: [],
  hotels: [],
  days: [newDay(1)],
  inclusions: [],
  exclusions: [],
  must_have: [],
  things_to_note: '',
  terms: '',
  total_amount: 0,
  amount_paid: '',
  subtotal: 0,
  commission_pct: 15,
  gst_on_commission_pct: 18,
  gst_visible_on_pdf: false,
  tcs_apply: true,
  tcs_pct: 2,
  tcs_visible_on_quote: false,
  booked: false,
  linked_invoice_id: null,
  payment_qr_image: '',
  payment_link: '',
  show_payment_breakup: true,
  show_pricing_on_pdf: true,
  payment_breakup: [],
  per_person_adult: '',
  per_person_child: '',
  per_person_infant: '',
  show_per_person_costs_on_pdf: false,
  font_family: 'Helvetica',
  body_font_size: 10,
  visibility: VISIBILITY_ITEMS.reduce((a, v) => ({ ...a, [v.key]: true }), {}),
  notes: '',
  seo_title: '',
  seo_description: '',
  seo_og_image: '',
});

/**
 * Dropdown of Trip Voucher actions. Rendered per itinerary card and inside
 * the editor top-bar. Keeps all voucher-flows in one visually-tight menu so
 * admins don't have to hunt for the right button.
 */
const VoucherActions = ({ it, onPreview, onDownload, onEmail, onWhatsApp, onReissue, size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  const btnCls = size === 'lg'
    ? 'text-sm px-3 py-1.5 border border-[#ffcc29] text-[#a06400] bg-[#fff8e1] rounded-md hover:bg-[#ffcc29]/40 flex items-center gap-1.5'
    : 'text-xs px-2.5 py-1.5 border border-[#ffcc29] text-[#a06400] bg-[#fff8e1] rounded-md hover:bg-[#ffcc29]/40 flex items-center gap-1';

  const item = 'w-full text-left text-xs px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700';
  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`voucher-actions-${it.id}`}
        className={btnCls}
        title="Voucher actions"
      >
        <ScrollText size={size === 'lg' ? 13 : 12} /> Voucher
        <ChevronDown size={size === 'lg' ? 12 : 11} className={`transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-30 py-1"
          data-testid={`voucher-menu-${it.id}`}
        >
          <button className={item} onClick={() => { setOpen(false); onPreview(it); }} data-testid={`voucher-preview-${it.id}`}>
            <Eye size={13} className="text-[#0d64a8]" />
            <span className="flex-1">Preview Voucher</span>
          </button>
          <button className={item} onClick={() => { setOpen(false); onDownload(it); }} data-testid={`voucher-download-${it.id}`}>
            <Download size={13} className="text-[#0d64a8]" />
            <span className="flex-1">Download PDF</span>
          </button>
          <button className={item} onClick={() => { setOpen(false); onEmail(it); }} data-testid={`voucher-email-${it.id}`}>
            <Mail size={13} className="text-[#0d64a8]" />
            <span className="flex-1">Email Voucher</span>
          </button>
          <div className="h-px bg-gray-100 my-1" />
          <button className={item} onClick={() => { setOpen(false); onWhatsApp(it); }} data-testid={`voucher-whatsapp-${it.id}`}>
            <MessageCircle size={13} className="text-[#25D366]" />
            <span className="flex-1">Send via WhatsApp</span>
          </button>
          {onReissue && (
            <>
              <div className="h-px bg-gray-100 my-1" />
              <button className={item} onClick={() => { setOpen(false); onReissue(it); }} data-testid={`voucher-reissue-${it.id}`}>
                <RotateCw size={13} className="text-orange-500" />
                <span className="flex-1 text-orange-600">Reissue Voucher</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ItineraryBuilder = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [copiedFor, setCopiedFor] = useState('');
  const [enquiries, setEnquiries] = useState([]);
  const [enqSearch, setEnqSearch] = useState('');
  const [showEnqDrop, setShowEnqDrop] = useState(false);
  const [packages, setPackages] = useState([]);
  const [showPkgPicker, setShowPkgPicker] = useState(false);
  const [pkgSearch, setPkgSearch] = useState('');
  const [pkgLoading, setPkgLoading] = useState(false);
  const [voucherPreview, setVoucherPreview] = useState(null); // itinerary object being previewed in the modal
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingMsg, setBookingMsg] = useState('');

  useEffect(() => { load(); }, []);

  // Payment Breakup Subtotal is always the sum of its line-items, and the
  // Pricing Subtotal one-way-mirrors it. So whenever `payment_breakup`
  // changes we push the fresh sum into `form.subtotal`.
  useEffect(() => {
    if (!form) return;
    const s = (form.payment_breakup || []).reduce((n, r) => n + (Number(r.amount) || 0), 0);
    if (Math.round(Number(form.subtotal || 0)) !== Math.round(s)) {
      setForm((f) => ({ ...f, subtotal: s }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.payment_breakup]);

  // Auto-calculate total nights from every hotel's check-in/out span.
  // This is the "expected" trip length used to auto-fit the day-wise plan.
  const calculatedNights = useMemo(() => {
    if (!form) return 0;
    let total = 0;
    for (const h of (form.hotels || [])) {
      if (!h.check_in || !h.check_out) continue;
      const inD = new Date(h.check_in), outD = new Date(h.check_out);
      if (isNaN(inD) || isNaN(outD) || outD <= inD) continue;
      const d = Math.round((outD - inD) / 86400000);
      if (d > 0) total += d;
    }
    return total;
  }, [form?.hotels]);

  // Auto-sync each hotel's `nights` field + the day-wise plan length to the
  // computed value. If a user has deliberately added extra days beyond the
  // hotel span (via the confirm-prompted "+ Add Day" button) we leave those
  // alone; we only ever grow the list to match hotel nights, never truncate
  // an admin's manual overflow.
  useEffect(() => {
    if (!form) return;
    // 1) Sync per-hotel `nights` from its own check_in/check_out (display only,
    //    stored on the hotel model — never overwrites when dates are blank).
    const nextHotels = (form.hotels || []).map((h) => {
      if (!h.check_in || !h.check_out) return h;
      const inD = new Date(h.check_in), outD = new Date(h.check_out);
      if (isNaN(inD) || isNaN(outD) || outD <= inD) return h;
      const d = Math.round((outD - inD) / 86400000);
      return d > 0 && d !== Number(h.nights) ? { ...h, nights: d } : h;
    });
    const hotelsChanged = nextHotels.some((h, i) => h !== (form.hotels || [])[i]);

    // 2) Grow the days array to at least `calculatedNights + 1` entries
    //    (N nights = N+1 travel days), but never truncate.
    const targetDays = calculatedNights > 0 ? calculatedNights + 1 : 0;
    const currentDays = (form.days || []).length;
    let nextDays = form.days;
    if (targetDays > currentDays) {
      const extra = Array.from({ length: targetDays - currentDays }, (_, k) => newDay(currentDays + k + 1));
      nextDays = [...(form.days || []), ...extra];
    }
    if (hotelsChanged || nextDays !== form.days) {
      setForm((f) => ({ ...f, hotels: nextHotels, days: nextDays }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedNights]);

  // Add-Day handler used by the days tab. Confirms with the admin if the new
  // day would exceed the nights auto-calculated from the hotel dates.
  const addDay = () => {
    const current = (form?.days || []).length;
    const expected = calculatedNights > 0 ? calculatedNights + 1 : 0;
    if (expected > 0 && current >= expected) {
      const ok = window.confirm('This is beyond the calculated nights. Add anyway?');
      if (!ok) return;
    }
    upd('days', [...(form.days || []), newDay(current + 1)]);
  };
  const load = async () => {
    setLoading(true);
    try {
      const [l, e, p] = await Promise.all([
        axios.get(`${API}/admin/itineraries`, { headers: adminAuthHeader() }),
        axios.get(`${API}/admin/enquiries`, { headers: adminAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/packages`).catch(() => ({ data: [] })),
      ]);
      setItems(l.data || []);
      setEnquiries(e.data || []);
      setPackages(p.data || []);
    } finally { setLoading(false); }
  };

  const filteredPkgs = useMemo(() => {
    const q = (pkgSearch || '').trim().toLowerCase();
    if (!q) return packages.slice(0, 30);
    return packages.filter((p) =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.pkg_code || '').toLowerCase().includes(q) ||
      (p.destination || '').toLowerCase().includes(q)
    ).slice(0, 30);
  }, [packages, pkgSearch]);

  const createFromPackage = async (pkg) => {
    setPkgLoading(true);
    try {
      const r = await axios.post(`${API}/admin/itineraries/from-package/${pkg.pkg_code}`, {}, { headers: adminAuthHeader() });
      setShowPkgPicker(false);
      setPkgSearch('');
      await load();
      startEdit(r.data);
    } catch (e) {
      window.alert('Failed to generate itinerary from package');
    } finally { setPkgLoading(false); }
  };

  const startNew = () => { setForm(emptyForm()); setEditing('new'); setActiveTab('overview'); };
  const startEdit = async (it) => {
    const res = await axios.get(`${API}/admin/itineraries/${it.id}`, { headers: adminAuthHeader() });
    const d = res.data;
    setForm({
      ...emptyForm(),
      ...d,
      customer: { ...emptyCustomer, ...(d.customer || {}) },
      days: (d.days && d.days.length) ? d.days : [newDay(1)],
      visibility: { ...emptyForm().visibility, ...(d.visibility || {}) },
      _id: d.id,
      _slug: d.slug,
      _public_token: d.public_token,
      _voucher_no: d.voucher_no,
    });
    setEditing(d.id);
    setActiveTab('overview');
  };
  const cancel = () => { setEditing(null); setForm(null); };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updCust = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const updVis = (k, v) => setForm((f) => ({ ...f, visibility: { ...f.visibility, [k]: v } }));

  const filteredEnquiries = useMemo(() => {
    const q = enqSearch.trim().toLowerCase();
    if (!q) return enquiries.slice(0, 8);
    return enquiries.filter((e) =>
      (e.client_name || '').toLowerCase().includes(q) ||
      (e.phone || '').toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q)
    ).slice(0, 10);
  }, [enquiries, enqSearch]);

  const pickEnquiry = (e) => {
    setForm((f) => ({ ...f, customer: { ...f.customer, name: e.client_name || f.customer.name, phone: e.phone || f.customer.phone, email: e.email || f.customer.email } }));
    setShowEnqDrop(false); setEnqSearch('');
  };

  const save = async (opts = {}) => {
    if (!form) return;
    // Date validation — block save on invalid trip or hotel date ranges.
    if (form.start_date && form.end_date && form.end_date <= form.start_date) {
      alert('Trip end date must be after start date.');
      return;
    }
    const badHotelIdx = (form.hotels || []).findIndex((h) => h.check_in && h.check_out && h.check_out <= h.check_in);
    if (badHotelIdx !== -1) {
      alert(`Hotel #${badHotelIdx + 1}: check-out must be after check-in.`);
      return;
    }
    if (!opts.silent) setSaving(true);
    try {
      const payload = { ...form };
      delete payload._id; delete payload._slug; delete payload._public_token; delete payload._voucher_no;
      payload.destinations = (payload.destinations || []).filter(Boolean);
      payload.inclusions = (payload.inclusions || []).filter(Boolean);
      payload.exclusions = (payload.exclusions || []).filter(Boolean);
      payload.must_have = (payload.must_have || []).filter(Boolean);
      payload.pax_adults = Number(payload.pax_adults) || 0;
      payload.pax_children = Number(payload.pax_children) || 0;
      payload.amount_paid = Number(payload.amount_paid) || 0;
      payload.subtotal = Number(payload.subtotal) || 0;
      payload.commission_pct = Number(payload.commission_pct) || 0;
      payload.gst_on_commission_pct = Number(payload.gst_on_commission_pct) || 0;
      payload.tcs_pct = Number(payload.tcs_pct) || 0;
      // Recompute total_amount (Grand Total) so the card price on the listing
      // always mirrors the current pricing — never a stale value that was last
      // typed into an old editable field.
      {
        const sub = payload.subtotal;
        const comm = Math.round(sub * payload.commission_pct / 100);
        const gst = Math.round(comm * payload.gst_on_commission_pct / 100);
        const tcs = payload.tcs_apply ? Math.round((sub + comm + gst) * payload.tcs_pct / 100) : 0;
        payload.total_amount = sub + comm + gst + tcs;
      }
      payload.gst_visible_on_pdf = !!payload.gst_visible_on_pdf;
      payload.tcs_apply = !!payload.tcs_apply;
      payload.tcs_visible_on_quote = !!payload.tcs_visible_on_quote;
      payload.show_payment_breakup = !!payload.show_payment_breakup;
      payload.show_pricing_on_pdf = payload.show_pricing_on_pdf !== false;
      payload.show_per_person_costs_on_pdf = !!payload.show_per_person_costs_on_pdf;
      payload.per_person_adult = Number(payload.per_person_adult) || 0;
      payload.per_person_child = Number(payload.per_person_child) || 0;
      payload.per_person_infant = Number(payload.per_person_infant) || 0;
      payload.payment_breakup = (payload.payment_breakup || [])
        .map((r) => ({ label: (r.label || '').trim(), amount: Number(r.amount) || 0 }))
        .filter((r) => r.label || r.amount);
      payload.days = (payload.days || []).map((d, i) => ({ ...d, day_number: i + 1 }));

      let saved;
      if (editing === 'new') {
        const r = await axios.post(`${API}/admin/itineraries`, payload, { headers: adminAuthHeader() });
        saved = r.data;
      } else {
        const r = await axios.put(`${API}/admin/itineraries/${editing}`, payload, { headers: adminAuthHeader() });
        saved = r.data;
      }
      // Stay in editor with fresh IDs for existing ones
      setForm((f) => ({ ...f, _id: saved.id, _slug: saved.slug, _public_token: saved.public_token }));
      setEditing(saved.id);
      await load();
    } finally { if (!opts.silent) setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete itinerary "${it.trip_title || 'Untitled'}"?`)) return;
    await axios.delete(`${API}/admin/itineraries/${it.id}`, { headers: adminAuthHeader() });
    await load();
  };

  const duplicate = async (it) => {
    const r = await axios.post(`${API}/admin/itineraries/${it.id}/duplicate`, {}, { headers: adminAuthHeader() });
    await load();
    startEdit(r.data);
  };

  const downloadPdf = async (it) => {
    const res = await axios.get(`${API}/admin/itineraries/${it.id}/pdf`, { headers: adminAuthHeader(), responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url;
    a.download = `Itinerary-${it.slug || 'trip'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 200);
  };

  const downloadVoucher = async (it) => {
    const res = await axios.get(`${API}/admin/itineraries/${it.id}/voucher-pdf`, { headers: adminAuthHeader(), responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url;
    a.download = `Voucher-${it.slug || 'trip'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 200);
  };

  const emailVoucher = async (it) => {
    // Trigger a PDF download first so the admin has the file ready to attach,
    // then open a pre-filled mail draft. This is the same pattern the itinerary
    // Email button uses (Resend integration is pending API keys).
    try { await downloadVoucher(it); } catch (_) { /* keep going even if download fails */ }
    const to = it.customer?.email || '';
    const bookingId = `TS-${String(it.id || '').slice(0, 8).toUpperCase()}`;
    const subject = `Trip Voucher - ${it.trip_title || 'Your booking'} (${bookingId})`;
    const dest = (it.destinations || []).join(', ') || 'your trip';
    const dates = it.start_date && it.end_date ? `${fmtDate(it.start_date)} to ${fmtDate(it.end_date)}` : 'your travel dates';
    const body = (
`Hi ${it.customer?.name || 'there'},

Please find attached the official Trip Voucher for ${dest} (${dates}).
This voucher is your booking confirmation and is required for hotel check-ins and tour pickups.

Booking ID: ${bookingId}

If you need any change, please call us on WhatsApp before making it on your own.

Warm regards,
Team Travelsera`
    );
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareVoucherWA = async (it) => {
    // Ensure the voucher has a stable number so the OG share URL resolves. This
    // is a no-op if the number already exists.
    let voucherNo = it.voucher_no;
    if (!voucherNo) {
      try {
        const res = await axios.post(`${API}/admin/itineraries/${it.id}/ensure-voucher-no`, {}, { headers: adminAuthHeader() });
        voucherNo = res.data?.voucher_no;
      } catch (_) { /* fall back below */ }
    }
    // WhatsApp will crawl `/api/share/voucher/{voucher_no}` and render a rich
    // preview card (trip banner + "Trip Voucher for {name}"). If for any reason
    // there is no voucher_no, fall back to the live itinerary link.
    const bookingId = `TS-${String(it.id || '').slice(0, 8).toUpperCase()}`;
    const dest = (it.destinations || []).join(', ') || 'your trip';
    const dates = it.start_date && it.end_date ? `${fmtDate(it.start_date)} to ${fmtDate(it.end_date)}` : 'your dates';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const voucherShareUrl = voucherNo ? `${origin}/api/share/voucher/${voucherNo}` : publicUrl(it);
    const label = voucherNo ? `Voucher ${voucherNo}` : `Booking ${bookingId}`;
    const text = (
`*Trip Voucher for ${it.customer?.name || 'You'}* ✈️

Hi ${it.customer?.name || 'there'}!

Your booking is confirmed 🎉
🌴 *${dest}*
📅 ${dates}
🎟️ ${label}

Tap to open your official Trip Voucher (PDF):
${voucherShareUrl}

Please carry a printed or digital copy at all check-ins.

_Team Travelsera 💛_`
    );
    const phone = (it.customer?.phone || '').replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const previewVoucher = (it) => {
    // Open the in-app modal (renders the PDF in an iframe). The modal itself
    // fetches the bytes so it also captures the freshly-assigned voucher_no
    // from the response's Content-Disposition header.
    setVoucherPreview(it);
  };

  const handlePackageBooked = async () => {
    if (!form._id) return;
    setBookingLoading(true); setBookingMsg('');
    try {
      // Persist any pricing edits BEFORE syncing so the backend uses the latest
      // subtotal / commission / gst / tcs values.
      await save({ silent: true }).catch(() => {});
      const res = await axios.post(
        `${API}/admin/itineraries/${form._id}/book`, {},
        { headers: adminAuthHeader() },
      );
      const t = res.data?.totals || {};
      const label = res.data?.created_new ? 'created' : 'updated';
      setBookingMsg(
        `Invoice ${res.data?.invoice_number || ''} ${label} and linked to ${form.customer?.phone}. ` +
        `Grand Total INR ${Number(t.grand_total || 0).toLocaleString('en-IN')} synced.`
      );
      upd('booked', true);
      upd('linked_invoice_id', res.data?.invoice_id);
    } catch (e) {
      setBookingMsg(e?.response?.data?.detail || 'Could not sync invoice. Please try again.');
    } finally { setBookingLoading(false); }
  };

  const reissueVoucher = async (it) => {
    // Ask for a short reason so the audit trail on `voided_voucher_nos`
    // captures WHY the number was voided (e.g. dates changed, hotel swapped).
    const reason = window.prompt(
      'Reissue voucher — what is the reason?\n\n' +
      '(e.g. "Dates changed", "Hotel swapped", "Extended by 2 days")\n' +
      'This reason is stored on the old voucher entry for audit and is not shown to the customer.'
    );
    if (reason === null) return;              // user cancelled
    const clean = reason.trim();
    if (!clean) {
      window.alert('A short reason is required to reissue a voucher.');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/itineraries/${it.id}/reissue-voucher`,
        { reason: clean },
        { headers: adminAuthHeader() },
      );
      await load();
      window.alert(`New voucher issued: ${res.data?.voucher_no}\nReason logged: "${clean}"`);
    } catch (_) {
      window.alert('Could not reissue voucher. Please try again.');
    }
  };

  const publicUrl = (it) => {
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    return `${backend}/api/itineraries/public/${it.slug}?token=${it.public_token}`;
  };

  const copyLink = async (it) => {
    try { await navigator.clipboard.writeText(publicUrl(it)); setCopiedFor(it.id); setTimeout(() => setCopiedFor(''), 1600); }
    catch (_) { window.prompt('Copy this link:', publicUrl(it)); }
  };

  const shareWA = (it) => {
    // Use the /api/share/itinerary URL so WhatsApp's crawler picks up the OG
    // tags (banner image + trip title) and renders a rich chat preview.
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const key = it.public_token || it.slug || it.id;
    const shareUrl = key ? `${origin}/api/share/itinerary/${key}` : publicUrl(it);
    const text = buildItineraryShareMessage(it, shareUrl);
    const phone = (it.customer?.phone || '').replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareEmail = (it) => {
    const to = it.customer?.email || '';
    const subject = `Your trip itinerary: ${it.trip_title}`;
    const body = `Hi ${it.customer?.name || 'there'},\n\nPlease find your full trip itinerary here:\n${publicUrl(it)}\n\nWe've included flights, hotels, day-wise plan, inclusions, and payment details.\n\nRegards,\nTravelsera`;
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // -------- Editor render --------
  if (editing !== null && form) {
    const Tab = ({ tab }) => (
      <button
        onClick={() => setActiveTab(tab.key)}
        data-testid={`it-tab-${tab.key}`}
        className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border transition whitespace-nowrap ${activeTab === tab.key ? 'bg-[#0d64a8] text-white border-[#0d64a8]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0d64a8] hover:text-[#0d64a8]'}`}
      >
        <tab.icon size={13}/> {tab.label}
      </button>
    );

    return (
      <>
      <div data-testid="itinerary-editor">
        {/* Top action bar */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{editing === 'new' ? 'New Itinerary' : (form.trip_title || 'Editing itinerary')}</h2>
            <p className="text-xs text-gray-500">Build the trip document section by section. Toggle any section off from the visibility panel below to hide it from the customer PDF.</p>
          </div>
          <div className="flex gap-2 items-center">
            {form._id && (
              <>
                <a href={publicUrl({ slug: form._slug, public_token: form._public_token })} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 border border-gray-200 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><Eye size={13}/> Preview PDF</a>
                <VoucherActions
                  it={{ id: form._id, slug: form._slug || 'trip', public_token: form._public_token, customer: form.customer, trip_title: form.trip_title, destinations: form.destinations, start_date: form.start_date, end_date: form.end_date, voucher_no: form._voucher_no }}
                  onPreview={previewVoucher}
                  onDownload={downloadVoucher}
                  onEmail={emailVoucher}
                  onWhatsApp={shareVoucherWA}
                  onReissue={form._voucher_no ? reissueVoucher : null}
                  size="lg"
                />
              </>
            )}
            <button onClick={cancel} className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-200 rounded-md"><X size={14} className="inline mr-1"/> Cancel</button>
            <button onClick={save} disabled={saving} data-testid="it-save" className="text-sm bg-[#0d64a8] hover:bg-[#0a548e] text-white px-4 py-1.5 rounded-md disabled:opacity-60 flex items-center gap-1.5">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} {saving ? 'Saving…' : 'Save Itinerary'}
            </button>
          </div>
        </div>

        {/* Visibility */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <div className="text-xs font-semibold text-gray-800 uppercase tracking-widest mb-3">Section Visibility · What appears in the customer PDF</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {VISIBILITY_ITEMS.map((v) => (
              <label key={v.key} data-testid={`vis-${v.key}`} className={`flex items-center justify-between px-3 py-2 rounded-md border text-xs cursor-pointer transition ${form.visibility[v.key] ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                <span className="flex items-center gap-1.5">{form.visibility[v.key] ? <Eye size={12}/> : <EyeOff size={12}/>} {v.label}</span>
                <input type="checkbox" checked={!!form.visibility[v.key]} onChange={(e) => updVis(v.key, e.target.checked)} className="accent-emerald-600"/>
              </label>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
          {TABS.map((t) => <Tab key={t.key} tab={t} />)}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Trip Title *</label>
                <input value={form.trip_title || ''} onChange={(e) => upd('trip_title', e.target.value)} placeholder="Bali Honeymoon for Sharma Family" data-testid="it-title" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                <input type="date" value={form.start_date || ''} onChange={(e) => upd('start_date', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={form.end_date || ''}
                  min={form.start_date || undefined}
                  onChange={(e) => upd('end_date', e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] ${form.start_date && form.end_date && form.end_date <= form.start_date ? 'border-red-400' : 'border-gray-200'}`}
                  data-testid="trip-end-date"
                />
                {form.start_date && form.end_date && form.end_date <= form.start_date && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="trip-date-warning">End date must be after start date.</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Adults</label>
                <input type="number" value={form.pax_adults} onChange={(e) => upd('pax_adults', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Children</label>
                <input type="number" value={form.pax_children} onChange={(e) => upd('pax_children', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Destinations (comma separated)</label>
                <input value={(form.destinations || []).join(', ')} onChange={(e) => upd('destinations', e.target.value.split(',').map((s) => s.trim()))} placeholder="Bali, Ubud, Nusa Penida" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-xs font-semibold text-gray-800 uppercase tracking-widest">Customer / Guest</div>
                <div className="relative">
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 focus-within:border-[#0d64a8]">
                    <Search size={13} className="text-gray-400"/>
                    <input placeholder="Search by name, phone or email…" value={enqSearch} onChange={(e) => { setEnqSearch(e.target.value); setShowEnqDrop(true); }} onFocus={() => setShowEnqDrop(true)} className="bg-transparent text-xs outline-none w-64" data-testid="cust-search-input"/>
                    <Users size={13} className="text-gray-400"/>
                  </div>
                  {showEnqDrop && filteredEnquiries.length > 0 && (
                    <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-20 max-h-72 overflow-auto" onMouseLeave={() => setShowEnqDrop(false)}>
                      {filteredEnquiries.map((e) => (
                        <button key={e.id} type="button" onClick={() => pickEnquiry(e)} className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0" data-testid={`cust-search-row-${e.id}`}>
                          <div className="text-sm font-medium text-gray-900" data-testid={`cust-search-name-${e.id}`}>{e.client_name || '—'}</div>
                          <div className="text-[11px] text-gray-500">
                            <span data-testid={`cust-search-phone-${e.id}`}>{e.phone || '—'}</span>
                            <span className="mx-1">·</span>
                            <span data-testid={`cust-search-email-${e.id}`}>{e.email || '—'}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input value={form.customer.name} onChange={(e) => updCust('name', e.target.value)} placeholder="Name" className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="it-cust-name"/>
                <input value={form.customer.phone} onChange={(e) => updCust('phone', e.target.value)} placeholder="Phone" className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
                <input value={form.customer.email} onChange={(e) => updCust('email', e.target.value)} placeholder="Email" className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-gray-800 uppercase tracking-widest mb-2">Cover Banner (top of PDF)</div>
              <p className="text-[11px] text-gray-500 mb-3">Landscape image that spans the full width of the PDF header, right below the Travelsera logo. Skip to auto-use the first day's photo.</p>
              <ImageUploader value={form.cover_image || ''} onChange={(url) => upd('cover_image', url)} label="Cover Banner" hint="1600×450 works best · JPG/PNG" height="h-36"/>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-800 uppercase tracking-widest">Greeting Message</label>
                <button onClick={() => upd('greeting', `Dear ${form.customer.name || 'Traveller'},\n\nWe're thrilled to share your personalised itinerary for ${((form.destinations||[]).join(', ')) || 'your trip'}. Every day has been thoughtfully planned so you can relax and enjoy every moment. Please review the details below, and reach out to us anytime — before or during your travel.\n\nWishing you a wonderful journey!`)} className="text-[11px] text-[#0d64a8] hover:underline">Use auto-template</button>
              </div>
              <textarea value={form.greeting || ''} onChange={(e) => upd('greeting', e.target.value)} rows={5} placeholder="Leave blank to use a friendly auto-generated greeting in the PDF." className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"/>
            </div>
          </div>
        )}

        {activeTab === 'flights' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">Attach flight card screenshots (e.g. from MakeMyTrip) — one image per leg.</p>
              <button onClick={() => upd('flights', [...(form.flights || []), newFlight()])} data-testid="add-flight" className="text-xs bg-white border border-gray-200 px-3 py-1.5 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><Plus size={12}/> Add flight</button>
            </div>
            {(form.flights || []).map((f, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <input value={f.label} onChange={(e) => upd('flights', form.flights.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Onward / Return / Connection" className="w-64 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:border-[#0d64a8]"/>
                  <button onClick={() => upd('flights', form.flights.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><Trash2 size={14}/></button>
                </div>
                <ImageUploader value={f.image} onChange={(url) => upd('flights', form.flights.map((x, j) => j === i ? { ...x, image: url } : x))} label="Flight card screenshot" hint="Landscape flight card image · JPG/PNG" height="h-40"/>
                <textarea value={f.notes || ''} onChange={(e) => upd('flights', form.flights.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))} rows={2} placeholder="Optional notes (e.g. Terminal, baggage limits)" className="w-full mt-3 border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"/>
              </div>
            ))}
            {(form.flights || []).length === 0 && <div className="text-center text-xs text-gray-400 py-8 border border-dashed border-gray-200 rounded-md">No flights added yet.</div>}
          </div>
        )}

        {activeTab === 'hotels' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">Add one entry per hotel. Up to 2 photos each will render beautifully on the PDF.</p>
              <button onClick={() => upd('hotels', [...(form.hotels || []), newHotel()])} data-testid="add-hotel" className="text-xs bg-white border border-gray-200 px-3 py-1.5 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><Plus size={12}/> Add hotel</button>
            </div>
            {(form.hotels || []).map((h, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-gray-800">Hotel #{i + 1}</div>
                  <button onClick={() => upd('hotels', form.hotels.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><Trash2 size={14}/></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div className="md:col-span-2"><input value={h.name} onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Hotel Name" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"/></div>
                  <input value={h.city} onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, city: e.target.value } : x))} placeholder="City" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"/>
                  <div><label className="block text-[10px] text-gray-500 mb-0.5">Check-in</label><input type="date" value={h.check_in || ''} onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, check_in: e.target.value } : x))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid={`hotel-${i}-check-in`}/></div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">Check-out</label>
                    <input
                      type="date"
                      value={h.check_out || ''}
                      min={h.check_in || undefined}
                      onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, check_out: e.target.value } : x))}
                      className={`w-full border rounded-md px-3 py-2 text-sm ${h.check_in && h.check_out && h.check_out <= h.check_in ? 'border-red-400' : 'border-gray-200'}`}
                      data-testid={`hotel-${i}-check-out`}
                    />
                    {h.check_in && h.check_out && h.check_out <= h.check_in && (
                      <p className="text-[11px] text-red-600 mt-0.5" data-testid={`hotel-${i}-date-warning`}>Check-out must be after check-in.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">Nights <span className="text-gray-400">(auto)</span></label>
                    <input
                      type="number"
                      value={h.nights || ''}
                      readOnly
                      placeholder="—"
                      className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700"
                      data-testid={`hotel-${i}-nights`}
                    />
                  </div>
                  <input value={h.room_type} onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, room_type: e.target.value } : x))} placeholder="Room type (e.g. Deluxe with balcony)" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm md:col-span-2"/>
                  <select value={h.meal_plan} onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, meal_plan: e.target.value } : x))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white">
                    {['CP','MAP','AP','EP','All Inclusive'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input value={h.booking_ref || ''} onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, booking_ref: e.target.value } : x))} placeholder="Booking ref (optional)" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"/>
                  <input value={h.map_link || ''} onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, map_link: e.target.value } : x))} placeholder="Google Maps link (optional)" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm md:col-span-2"/>
                  <div className="md:col-span-3">
                    <label className="block text-[10px] text-gray-500 mb-0.5">Early Check-in / Late Check-out Options (optional)</label>
                    <input
                      value={h.early_late_options || ''}
                      onChange={(e) => upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, early_late_options: e.target.value } : x))}
                      placeholder="e.g. Early check-in from 10 AM available on request · Late check-out until 4 PM (₹1500)"
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                      data-testid={`hotel-${i}-early-late-options`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[0, 1].map((k) => (
                    <ImageUploader
                      key={k}
                      value={(h.images || [])[k] || ''}
                      onChange={(url) => {
                        const imgs = [...(h.images || [])];
                        imgs[k] = url;
                        upd('hotels', form.hotels.map((x, j) => j === i ? { ...x, images: imgs.filter(Boolean) } : x));
                      }}
                      label={`Hotel Photo ${k + 1}`}
                      hint="Landscape · 800×600 or larger"
                      height="h-32"
                    />
                  ))}
                </div>
              </div>
            ))}
            {(form.hotels || []).length === 0 && <div className="text-center text-xs text-gray-400 py-8 border border-dashed border-gray-200 rounded-md">No hotels added yet.</div>}
          </div>
        )}

        {activeTab === 'days' && (
          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-gray-800 uppercase tracking-widest">Typography</span>
              <label className="text-xs text-gray-600 flex items-center gap-2">Font
                <select value={form.font_family || 'Helvetica'} onChange={(e) => upd('font_family', e.target.value)} data-testid="font-family" className="border border-gray-200 rounded-md px-2 py-1 text-xs bg-white">
                  <option value="Helvetica">Helvetica (default)</option>
                  <option value="Times">Times</option>
                  <option value="Courier">Courier</option>
                </select>
              </label>
              <label className="text-xs text-gray-600 flex items-center gap-2">Body size
                <select value={form.body_font_size || 10} onChange={(e) => upd('body_font_size', Number(e.target.value))} data-testid="font-size" className="border border-gray-200 rounded-md px-2 py-1 text-xs bg-white">
                  {[9, 10, 11, 12, 13].map((s) => <option key={s} value={s}>{s}pt</option>)}
                </select>
              </label>
              <span className="text-[11px] text-gray-500 ml-auto">Use <code>**bold**</code>, <code>*italic*</code>, and start a line with <code>-</code> for bullets.</span>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500" data-testid="days-info">
                Plan each day with a title, 2–4 photos and a detailed description. Drag-and-drop reorder is not needed — day numbers auto-adjust.
                {calculatedNights > 0 && (
                  <span className="ml-1 text-gray-700">
                    Hotels span <strong data-testid="calc-nights">{calculatedNights}</strong> night{calculatedNights === 1 ? '' : 's'} → auto-fitted to <strong>{calculatedNights + 1}</strong> day{calculatedNights === 0 ? '' : 's'}.
                  </span>
                )}
              </p>
              <button onClick={addDay} data-testid="add-day" className="text-xs bg-white border border-gray-200 px-3 py-1.5 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><Plus size={12}/> Add day</button>
            </div>
            {(form.days || []).map((d, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-[#0d64a8] text-white text-xs font-bold flex items-center justify-center">D{i + 1}</span>
                    <input value={d.title} onChange={(e) => upd('days', form.days.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Arrival & Welcome to Bali" className="w-96 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:border-[#0d64a8]" data-testid={`day-title-${i}`}/>
                    <input type="date" value={d.date || ''} onChange={(e) => upd('days', form.days.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} className="border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:border-[#0d64a8]"/>
                  </div>
                  <button onClick={() => upd('days', form.days.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><Trash2 size={14}/></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  {[0, 1, 2, 3].map((k) => (
                    <ImageUploader
                      key={k}
                      value={(d.images || [])[k] || ''}
                      onChange={(url) => {
                        const imgs = [...(d.images || [])];
                        imgs[k] = url;
                        upd('days', form.days.map((x, j) => j === i ? { ...x, images: imgs.filter(Boolean) } : x));
                      }}
                      label={`Photo ${k + 1}`}
                      hint=""
                      height="h-24"
                    />
                  ))}
                </div>
                <textarea value={d.description} onChange={(e) => upd('days', form.days.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} rows={5} placeholder={"Detailed plan for the day. Use bullet points (start line with '- ') for a bulleted list on the PDF."} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" data-testid={`day-desc-${i}`}/>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <span className="text-[11px] text-gray-500">Meals included:</span>
                  {MEALS.map((m) => (
                    <label key={m} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border cursor-pointer ${(d.meals_included || []).includes(m) ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-gray-500 border-gray-200'}`}>
                      <input type="checkbox" checked={(d.meals_included || []).includes(m)} onChange={(e) => {
                        const cur = new Set(d.meals_included || []);
                        if (e.target.checked) cur.add(m); else cur.delete(m);
                        upd('days', form.days.map((x, j) => j === i ? { ...x, meals_included: Array.from(cur) } : x));
                      }} className="accent-emerald-600"/>
                      {m}
                    </label>
                  ))}
                </div>

                {/* Timed activities — used by the Trip Voucher PDF */}
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold text-[#0d64a8] uppercase tracking-widest">Timed Activities <span className="text-gray-400 font-normal">(voucher)</span></span>
                    <button
                      type="button"
                      onClick={() => upd('days', form.days.map((x, j) => j === i ? { ...x, activities: [...(x.activities || []), { time: '', title: '' }] } : x))}
                      className="text-[11px] text-[#0d64a8] hover:text-[#0a548e] flex items-center gap-1"
                      data-testid={`day-add-activity-${i}`}
                    >
                      <Plus size={11} /> Add activity
                    </button>
                  </div>
                  {(d.activities || []).length === 0 && (
                    <p className="text-[11px] text-gray-400 italic">No timed activities yet. These appear on the Trip Voucher as a "10:00 AM · City Tour" style list.</p>
                  )}
                  {(d.activities || []).map((a, ai) => {
                    // Parse the existing "HH:MM AM/PM" free-text into structured parts.
                    // Falls back to blanks when the string is empty or non-conforming.
                    const parseTime = (t) => {
                      const m = /^\s*(\d{1,2})[:.]?(\d{0,2})\s*(AM|PM|am|pm)?\s*$/.exec(t || '');
                      if (!m) return { h: '', mm: '', ap: 'AM' };
                      return { h: String(parseInt(m[1], 10) || ''), mm: (m[2] || '').padStart(2, '0') || '', ap: (m[3] || 'AM').toUpperCase() };
                    };
                    const composeTime = (h, mm, ap) => {
                      if (!h && !mm) return '';
                      return `${h || '12'}:${(mm || '00').padStart(2, '0')} ${ap || 'AM'}`;
                    };
                    const parts = parseTime(a.time);
                    const setPart = (patch) => {
                      const next = { ...parts, ...patch };
                      const newTime = composeTime(next.h, next.mm, next.ap);
                      upd('days', form.days.map((x, j) => j === i ? { ...x, activities: (x.activities || []).map((y, k) => k === ai ? { ...y, time: newTime } : y) } : x));
                    };
                    return (
                    <div key={ai} className="flex items-center gap-2 mb-1.5 flex-wrap" data-testid={`day-${i}-activity-${ai}`}>
                      <div className="flex items-center gap-1" data-testid={`day-${i}-activity-${ai}-time`}>
                        <select
                          value={parts.h}
                          onChange={(e) => setPart({ h: e.target.value })}
                          className="border border-gray-200 rounded-md px-1.5 py-1 text-xs bg-white focus:border-[#0d64a8]"
                          data-testid={`day-${i}-activity-${ai}-hour`}
                        >
                          <option value="">HH</option>
                          {Array.from({ length: 12 }, (_, k) => String(k + 1)).map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <span className="text-xs text-gray-400">:</span>
                        <select
                          value={parts.mm}
                          onChange={(e) => setPart({ mm: e.target.value })}
                          className="border border-gray-200 rounded-md px-1.5 py-1 text-xs bg-white focus:border-[#0d64a8]"
                          data-testid={`day-${i}-activity-${ai}-minute`}
                        >
                          <option value="">MM</option>
                          {Array.from({ length: 12 }, (_, k) => String(k * 5).padStart(2, '0')).map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => setPart({ ap: parts.ap === 'AM' ? 'PM' : 'AM' })}
                          className={`text-[10px] font-semibold uppercase rounded-md px-2 py-1 border transition-colors ${parts.ap === 'PM' ? 'bg-[#0d64a8] border-[#0d64a8] text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-[#0d64a8]'}`}
                          data-testid={`day-${i}-activity-${ai}-ampm`}
                          title={`Switch to ${parts.ap === 'AM' ? 'PM' : 'AM'}`}
                        >
                          {parts.ap || 'AM'}
                        </button>
                      </div>
                      <input
                        value={a.title || ''}
                        onChange={(e) => upd('days', form.days.map((x, j) => j === i ? { ...x, activities: (x.activities || []).map((y, k) => k === ai ? { ...y, title: e.target.value } : y) } : x))}
                        placeholder="City Tour — Kuta Beach & Waterbom Park"
                        className="flex-1 min-w-[160px] border border-gray-200 rounded-md px-2 py-1 text-xs focus:border-[#0d64a8]"
                      />
                      <button
                        type="button"
                        onClick={() => upd('days', form.days.map((x, j) => j === i ? { ...x, activities: (x.activities || []).filter((_, k) => k !== ai) } : x))}
                        className="text-gray-400 hover:text-red-500 shrink-0"
                        title="Remove activity"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'inclusions' && (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest mb-2">Inclusions</div>
              <textarea value={(form.inclusions || []).join('\n')} onChange={(e) => upd('inclusions', e.target.value.split('\n'))} rows={10} placeholder={"One item per line, e.g.:\nDaily breakfast\nAirport transfers\n2 sightseeing tours"} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"/>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-rose-700 uppercase tracking-widest mb-2">Exclusions</div>
              <textarea value={(form.exclusions || []).join('\n')} onChange={(e) => upd('exclusions', e.target.value.split('\n'))} rows={10} placeholder={"One item per line, e.g.:\nInternational airfare\nVisa fees\nPersonal expenses"} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"/>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-[#0d64a8] uppercase tracking-widest mb-2">Must-Have / Pack List</div>
              <textarea value={(form.must_have || []).join('\n')} onChange={(e) => upd('must_have', e.target.value.split('\n'))} rows={10} placeholder={"One item per line, e.g.:\nPassport with 6-month validity\nSunscreen SPF 50+\nLight jackets for evenings"} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"/>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4" data-testid="things-to-note-panel">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <div className="text-xs font-semibold text-amber-700 uppercase tracking-widest">Things to Note</div>
                <p className="text-[11px] text-gray-500 mt-0.5">Any general advice, tips or reminders you want highlighted on the PDF (below Must-Have).</p>
              </div>
              <span className="text-[10px] text-gray-400">Supports **bold**, *italic* and line breaks</span>
            </div>
            <textarea
              value={form.things_to_note || ''}
              onChange={(e) => upd('things_to_note', e.target.value)}
              rows={5}
              data-testid="things-to-note-input"
              placeholder={"e.g.\nCarry a valid photo ID at all times.\nWater sports subject to weather clearance by local authorities.\n**Peak season surcharge may apply** on some dates."}
              className="w-full border border-amber-200 bg-amber-50/40 rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:bg-white resize-none"
            />
          </div>
          </>
        )}

        {activeTab === 'payment' && (
          <div className="space-y-4">
            {(() => {
              // Single source of truth for the Grand Total, shared between the
              // Payment Breakup card (as "Total Amount") and the Pricing card.
              const sub = Number(form.subtotal || 0);
              const comm = Math.round(sub * (Number(form.commission_pct ?? 15)) / 100);
              const gst = Math.round(comm * (Number(form.gst_on_commission_pct ?? 18)) / 100);
              const tcs = form.tcs_apply ? Math.round((sub + comm + gst) * (Number(form.tcs_pct ?? 2)) / 100) : 0;
              const grand = sub + comm + gst + tcs;
              const balance = Math.max(grand - Number(form.amount_paid || 0), 0);
              return (
            <div className="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Total Amount (INR)</label>
                <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm font-semibold text-gray-900" data-testid="payment-total-amount">
                  INR {grand.toLocaleString('en-IN')}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Linked with Pricing → Grand Total.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount Paid (INR)</label>
                <input type="number" step="1" min="0" value={form.amount_paid || ''} onChange={(e) => upd('amount_paid', e.target.value === '' ? '' : Number(e.target.value))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Balance Due</label>
                <div
                  className={`w-full rounded-md px-3 py-2 text-sm font-bold border-2 ${balance > 0 ? 'bg-red-50 border-red-300 text-red-700 shadow-sm' : 'bg-green-50 border-green-300 text-green-700'}`}
                  data-testid="payment-balance-due"
                >
                  INR {balance.toLocaleString('en-IN')}
                  {balance === 0 && <span className="ml-2 text-[10px] font-medium uppercase tracking-widest">Fully paid</span>}
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Link (Razorpay/Stripe/Any gateway URL)</label>
                <input value={form.payment_link || ''} onChange={(e) => upd('payment_link', e.target.value)} placeholder="https://rzp.io/l/xyz123" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"/>
                <p className="text-[11px] text-gray-500 mt-1">Renders as a "Pay Now Online" button in the PDF.</p>
              </div>
              <div className="md:col-span-2">
                <div
                  className="w-full border border-dashed border-gray-200 bg-gray-50 rounded-md px-3 py-3 text-xs text-gray-500"
                  data-testid="global-qr-note"
                >
                  Payment QR code is now managed globally under <span className="font-semibold text-gray-700">Billing → Invoice Settings</span>. Upload once and it appears on every itinerary's PDF.
                </div>
              </div>
              <div className="md:col-span-3">
                <div
                  className="text-xs italic text-gray-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                  data-testid="taxes-included-note"
                >
                  Total cost is inclusive of all applicable taxes.
                </div>
              </div>
            </div>
              );
            })()}

            {/* Per Person Cost Breakup — free-form manual entries (no auto-calc,
                no links). Toggled independently on the customer PDF. */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="per-person-cost-panel">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                  <div className="text-xs font-semibold text-gray-800 uppercase tracking-widest">Per Person Cost Breakup</div>
                  <p className="text-[11px] text-gray-500 mt-0.5">Manually enter the per-person price for each traveller category. Not linked to any other field or calculation.</p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="toggle-per-person-on-pdf">
                  <span className="text-xs text-gray-600">{form.show_per_person_costs_on_pdf ? 'Shown in Customer PDF' : 'Hidden from Customer PDF'}</span>
                  <span
                    onClick={() => upd('show_per_person_costs_on_pdf', !form.show_per_person_costs_on_pdf)}
                    className={`relative inline-block w-10 h-5 rounded-full transition-colors ${form.show_per_person_costs_on_pdf ? 'bg-[#0d64a8]' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.show_per_person_costs_on_pdf ? 'translate-x-5' : ''}`}/>
                  </span>
                </label>
              </div>
              <div className="overflow-hidden border border-gray-200 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider w-1/2">Category</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">Price (INR)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { key: 'per_person_adult', label: 'Adults' },
                      { key: 'per_person_child', label: 'Children' },
                      { key: 'per_person_infant', label: 'Infant' },
                    ].map((row) => (
                      <tr key={row.key}>
                        <td className="px-3 py-2 text-gray-800">{row.label}</td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={form[row.key] || ''}
                            onChange={(e) => upd(row.key, e.target.value === '' ? '' : Number(e.target.value))}
                            className="w-40 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:border-[#0d64a8]"
                            data-testid={`per-person-${row.key.replace('per_person_', '')}-input`}
                          />
                        </td>
                      </tr>
                    ))}
                    {(() => {
                      const adults = Number(form.pax_adults) || 0;
                      const children = Number(form.pax_children) || 0;
                      const infants = Number(form.pax_infants) || 0;
                      const calcTotal =
                        (Number(form.per_person_adult) || 0) * adults +
                        (Number(form.per_person_child) || 0) * children +
                        (Number(form.per_person_infant) || 0) * infants;
                      return (
                        <tr className="bg-gray-50">
                          <td className="px-3 py-2 text-xs font-semibold text-gray-700 uppercase tracking-widest">
                            Calculated Total
                            <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400 mt-0.5">
                              ({adults} adult{adults === 1 ? '' : 's'} × A) + ({children} child{children === 1 ? '' : 'ren'} × C) + ({infants} infant{infants === 1 ? '' : 's'} × I)
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div
                              className="w-40 border border-gray-200 bg-white rounded-md px-3 py-1.5 text-sm font-semibold text-gray-900"
                              data-testid="per-person-calc-total"
                            >
                              INR {calcTotal.toLocaleString('en-IN')}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
              {(() => {
                const adults = Number(form.pax_adults) || 0;
                const children = Number(form.pax_children) || 0;
                const infants = Number(form.pax_infants) || 0;
                const calcTotal =
                  (Number(form.per_person_adult) || 0) * adults +
                  (Number(form.per_person_child) || 0) * children +
                  (Number(form.per_person_infant) || 0) * infants;
                const subtotal = Number(form.subtotal) || 0;
                const commission = Math.round(subtotal * (Number(form.commission_pct) || 0) / 100);
                const gst = Math.round(commission * (Number(form.gst_on_commission_pct) || 0) / 100);
                const packageCost = subtotal + commission + gst;
                const matches = Math.round(calcTotal) === Math.round(packageCost);
                return (
                  <div className="mt-3 space-y-2">
                    <div
                      className={`flex items-center gap-2 text-xs font-medium rounded-md px-3 py-2 border ${matches ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}
                      data-testid="per-person-match-status"
                      data-match={matches ? 'true' : 'false'}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full ${matches ? 'bg-green-500' : 'bg-red-500'}`}/>
                      {matches
                        ? 'Matches Package Cost'
                        : 'Does not match Package Cost — please review'}
                      <span className="ml-auto text-[11px] text-gray-500 font-normal">
                        Calculated INR {calcTotal.toLocaleString('en-IN')} · Package Cost INR {packageCost.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div
                      className="flex items-center justify-between gap-3 text-xs rounded-md px-3 py-2 border border-gray-200 bg-gray-50"
                      data-testid="per-person-difference"
                    >
                      <span className="text-gray-600 font-medium">Difference <span className="text-gray-400 font-normal">(Package Cost − Calculated Total)</span></span>
                      <span className={`font-semibold ${(packageCost - calcTotal) === 0 ? 'text-gray-900' : (packageCost - calcTotal) > 0 ? 'text-amber-700' : 'text-red-700'}`}>
                        INR {(packageCost - calcTotal).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Payment Breakup — itemised charges shown ONLY in the admin form.
                This section is NEVER included in the customer PDF. */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="payment-breakup-panel">
              <div className="mb-2">
                <div className="text-xs font-semibold text-gray-800 uppercase tracking-widest">Payment Breakup</div>
                <p className="text-[11px] text-gray-500 mt-0.5">Enter itemised charges (e.g. Hotel, Flight, Sightseeing). The sum drives the Pricing Subtotal below. This section is not shown on any customer PDF.</p>
              </div>

              <div className="mt-2 space-y-2">
                  {(form.payment_breakup || []).map((row, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`breakup-row-${i}`}>
                      <input
                        value={row.label || ''}
                        onChange={(e) => upd('payment_breakup', (form.payment_breakup || []).map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                        placeholder="e.g. Hotel stay (5 nights)"
                        className="col-span-8 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:border-[#0d64a8]"
                      />
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={row.amount || ''}
                        onChange={(e) => upd('payment_breakup', (form.payment_breakup || []).map((x, j) => j === i ? { ...x, amount: e.target.value === '' ? '' : Number(e.target.value) } : x))}
                        placeholder="Amount"
                        className="col-span-3 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:border-[#0d64a8]"
                      />
                      <button
                        onClick={() => upd('payment_breakup', (form.payment_breakup || []).filter((_, j) => j !== i))}
                        data-testid={`breakup-remove-${i}`}
                        className="col-span-1 text-gray-400 hover:text-red-500 flex justify-center"
                        title="Remove row"
                      >
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-1">
                    <button
                      onClick={() => upd('payment_breakup', [...(form.payment_breakup || []), { label: '', amount: '' }])}
                      data-testid="breakup-add"
                      className="text-xs text-[#0d64a8] hover:text-[#0a548e] flex items-center gap-1 border border-dashed border-[#0d64a8]/40 rounded-md px-2 py-1"
                    >
                      <Plus size={12}/> Add line item
                    </button>
                    <div className="text-xs text-gray-600 flex items-center gap-2" data-testid="breakup-subtotal">
                      <span>Subtotal:</span>
                      <span className="text-sm font-semibold text-gray-900" data-testid="breakup-subtotal-value">
                        INR {Number(form.subtotal || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>
            </div>

            {/* Commission / GST / TCS / Grand Total — INTERNAL fields for accounting.
                Commission is NEVER shown on the customer PDF. GST and TCS have
                individual visibility toggles. */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4" data-testid="pricing-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-semibold text-gray-800 uppercase tracking-widest">Pricing</div>
                  <p className="text-[11px] text-gray-500 mt-0.5">Commission, GST and TCS are computed on the Subtotal below. Commission stays internal and never appears on any customer PDF.</p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="toggle-pricing-on-pdf">
                  <span className="text-xs text-gray-600">{form.show_pricing_on_pdf ? 'Pricing shown on PDF' : 'Pricing hidden from PDF'}</span>
                  <span
                    onClick={() => upd('show_pricing_on_pdf', !form.show_pricing_on_pdf)}
                    className={`relative inline-block w-10 h-5 rounded-full transition-colors ${form.show_pricing_on_pdf ? 'bg-[#0d64a8]' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.show_pricing_on_pdf ? 'translate-x-5' : ''}`}/>
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Subtotal (INR)</label>
                  <div
                    className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm font-semibold text-gray-900"
                    data-testid="pricing-subtotal"
                    title="Reads from Payment Breakup line items (one-way sync)."
                  >
                    INR {Number(form.subtotal || 0).toLocaleString('en-IN')}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Reads from Payment Breakup line items. Edit line items there to change this value.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Commission (%) <span className="text-gray-400">· internal</span></label>
                  <input
                    type="number" step="0.1" min="0"
                    value={form.commission_pct ?? 15}
                    onChange={(e) => upd('commission_pct', Number(e.target.value) || 0)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"
                    data-testid="pricing-commission-pct"
                  />
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-widest text-gray-500">Commission Amount</div>
                  <div className="text-lg font-bold text-gray-900" data-testid="pricing-commission-amount">
                    INR {Math.round((Number(form.subtotal || 0)) * (Number(form.commission_pct ?? 15)) / 100).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* GST row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center pt-3 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">GST on Commission (%)</label>
                  <input
                    type="number" step="0.1" min="0"
                    value={form.gst_on_commission_pct ?? 18}
                    onChange={(e) => upd('gst_on_commission_pct', Number(e.target.value) || 0)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"
                    data-testid="pricing-gst-pct"
                  />
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="toggle-gst-visible">
                  <span className="text-xs text-gray-600">{form.gst_visible_on_pdf ? 'GST shown on PDF' : 'GST hidden from PDF'}</span>
                  <span
                    onClick={() => upd('gst_visible_on_pdf', !form.gst_visible_on_pdf)}
                    className={`relative inline-block w-10 h-5 rounded-full transition-colors ${form.gst_visible_on_pdf ? 'bg-[#0d64a8]' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.gst_visible_on_pdf ? 'translate-x-5' : ''}`}/>
                  </span>
                </label>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-widest text-gray-500">GST Amount</div>
                  <div className="text-lg font-bold text-gray-900" data-testid="pricing-gst-amount">
                    INR {Math.round(((Number(form.subtotal || 0)) * (Number(form.commission_pct ?? 15)) / 100) * (Number(form.gst_on_commission_pct ?? 18)) / 100).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* TCS row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center pt-3 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">TCS (%)</label>
                  <input
                    type="number" step="0.1" min="0"
                    value={form.tcs_pct ?? 2}
                    onChange={(e) => upd('tcs_pct', Number(e.target.value) || 0)}
                    disabled={!form.tcs_apply}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] disabled:bg-gray-50 disabled:text-gray-400"
                    data-testid="pricing-tcs-pct"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="toggle-tcs-apply">
                    <span className="text-xs text-gray-600">{form.tcs_apply ? 'TCS applied' : 'TCS not applied'}</span>
                    <span
                      onClick={() => upd('tcs_apply', !form.tcs_apply)}
                      className={`relative inline-block w-10 h-5 rounded-full transition-colors ${form.tcs_apply ? 'bg-[#0d64a8]' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.tcs_apply ? 'translate-x-5' : ''}`}/>
                    </span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="toggle-tcs-visible">
                    <span className="text-xs text-gray-600">{form.tcs_visible_on_quote ? 'TCS shown on quote' : 'TCS hidden from quote'}</span>
                    <span
                      onClick={() => upd('tcs_visible_on_quote', !form.tcs_visible_on_quote)}
                      className={`relative inline-block w-10 h-5 rounded-full transition-colors ${form.tcs_visible_on_quote ? 'bg-[#0d64a8]' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.tcs_visible_on_quote ? 'translate-x-5' : ''}`}/>
                    </span>
                  </label>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-widest text-gray-500">TCS Amount</div>
                  <div className="text-lg font-bold text-gray-900" data-testid="pricing-tcs-amount">
                    INR {(() => {
                      const s = Number(form.subtotal || 0);
                      const c = Math.round(s * (Number(form.commission_pct ?? 15)) / 100);
                      const g = Math.round(c * (Number(form.gst_on_commission_pct ?? 18)) / 100);
                      return form.tcs_apply ? Math.round((s + c + g) * (Number(form.tcs_pct ?? 2)) / 100).toLocaleString('en-IN') : '0';
                    })()}
                  </div>
                </div>
              </div>

              {/* Grand total */}
              {(() => {
                const sub = Number(form.subtotal || 0);
                const comm = Math.round(sub * (Number(form.commission_pct ?? 15)) / 100);
                const gst = Math.round(comm * (Number(form.gst_on_commission_pct ?? 18)) / 100);
                const tcs = form.tcs_apply ? Math.round((sub + comm + gst) * (Number(form.tcs_pct ?? 2)) / 100) : 0;
                const grand = sub + comm + gst + tcs;
                return (
                  <div className="pt-3 mt-2 border-t-2 border-[#ffcc29] bg-gradient-to-r from-[#fff8e1] to-white rounded-md p-3 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#a06400] font-semibold">Grand Total</div>
                      <div className="text-[11px] text-gray-500">Subtotal + Commission + GST + TCS</div>
                    </div>
                    <div className="text-2xl font-extrabold text-[#0d64a8]" data-testid="pricing-grand-total">
                      INR {grand.toLocaleString('en-IN')}
                    </div>
                  </div>
                );
              })()}

              {/* Package Booked action */}
              <div className="pt-3 mt-2 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-gray-800">Ready to finalise?</div>
                  <p className="text-[11px] text-gray-500 mt-0.5">Syncs the Grand Total into an invoice linked to <span className="font-mono">{form.customer?.phone || '(no phone)'}</span>.</p>
                </div>
                <button
                  disabled={!form._id || !form.customer?.phone || bookingLoading}
                  onClick={handlePackageBooked}
                  data-testid="package-booked-btn"
                  className={`text-sm font-semibold px-4 py-2 rounded-md flex items-center gap-2 ${form.booked ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-[#0d64a8] hover:bg-[#0a548e] text-white'} disabled:opacity-60`}
                >
                  {bookingLoading ? <Loader2 size={14} className="animate-spin"/> : (form.booked ? <Check size={14}/> : <IndianRupee size={14}/>)}
                  {form.booked ? 'Booked — Re-sync Invoice' : 'Package Booked'}
                </button>
              </div>
              {bookingMsg && (
                <div className="text-xs bg-green-50 border border-green-200 text-green-700 rounded-md px-3 py-2" data-testid="booking-confirm-msg">
                  {bookingMsg}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'terms' && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <label className="block text-xs font-semibold text-gray-800 uppercase tracking-widest mb-2">Terms & Conditions</label>
            <p className="text-[11px] text-gray-500 mb-2">One line per point. Leave blank to fall back to the T&Cs from your Invoice Settings.</p>
            <textarea value={form.terms || ''} onChange={(e) => upd('terms', e.target.value)} rows={12} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none font-mono" data-testid="it-terms"/>
          </div>
        )}
      </div>
      <VoucherPreviewModal
        it={voucherPreview}
        open={!!voucherPreview}
        onClose={() => setVoucherPreview(null)}
        onDownload={downloadVoucher}
        onEmail={emailVoucher}
        onWhatsApp={shareVoucherWA}
      />
    </>
  );
  }

  // -------- List view --------
  return (
    <div data-testid="itineraries-manager">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Itinerary Builder</h2>
          <p className="text-sm text-gray-500">Craft day-wise trip documents with flights, hotels, day-plans, payments & share via PDF or link.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <button
              onClick={() => setShowPkgPicker((v) => !v)}
              data-testid="it-from-pkg-btn"
              className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900 text-sm px-4 py-2 rounded-md"
            >
              <Wand2 size={14}/> Auto-Generate from Package
            </button>
            {showPkgPicker && (
              <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-xl z-30" data-testid="pkg-picker">
                <div className="p-3 border-b border-gray-100">
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 focus-within:border-[#0d64a8]">
                    <Search size={14} className="text-gray-400"/>
                    <input
                      autoFocus
                      value={pkgSearch}
                      onChange={(e) => setPkgSearch(e.target.value)}
                      placeholder="Search packages by title, code or destination…"
                      className="bg-transparent text-sm outline-none w-full"
                      data-testid="pkg-picker-search"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">Pick a package to pre-fill hotels, flights, inclusions, cover image and pricing. You can edit everything afterwards.</p>
                </div>
                <div className="max-h-80 overflow-auto py-1">
                  {filteredPkgs.length === 0 ? (
                    <div className="text-center text-xs text-gray-400 py-6">No packages match.</div>
                  ) : filteredPkgs.map((p) => (
                    <button
                      key={p.pkg_code}
                      onClick={() => createFromPackage(p)}
                      disabled={pkgLoading}
                      data-testid={`pkg-pick-${p.pkg_code}`}
                      className="w-full text-left px-3 py-2 hover:bg-amber-50/60 border-b border-gray-50 last:border-0 flex items-center gap-3 disabled:opacity-60"
                    >
                      <img src={resolveImg(p.image) || ''} alt="" className="w-14 h-10 object-cover rounded-md bg-gray-100 shrink-0" onError={(e) => { e.currentTarget.style.visibility='hidden'; }}/>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{p.title}</div>
                        <div className="text-[11px] text-gray-500 truncate">#{p.pkg_code} · {p.destination || '—'} · {p.duration}</div>
                      </div>
                      <div className="text-[11px] text-[#0d64a8] font-semibold shrink-0">INR {Math.round(Number(p.price || 0)).toLocaleString('en-IN')}</div>
                    </button>
                  ))}
                </div>
                <div className="px-3 py-2 border-t border-gray-100 text-right">
                  <button onClick={() => setShowPkgPicker(false)} className="text-xs text-gray-500 hover:text-gray-800">Close</button>
                </div>
              </div>
            )}
          </div>
          <button onClick={startNew} data-testid="it-new-btn" className="flex items-center gap-1.5 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-4 py-2 rounded-md"><Plus size={14}/> New Itinerary</button>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mb-4">
          <AdminSearch value={q} onChange={setQ} placeholder="Search voucher no, customer name, phone, trip title, destination..." testId="it-search" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl py-14 text-center">
          <FileText className="mx-auto text-gray-300 mb-3" size={48}/>
          <div className="text-gray-800 font-semibold">No itineraries yet</div>
          <p className="text-xs text-gray-500 mt-1">Click <strong>New Itinerary</strong> to plan your first trip.</p>
          <button onClick={startNew} className="mt-4 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-5 py-2 rounded-md">Create your first itinerary</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.filter((it) => {
            const s = q.trim().toLowerCase();
            if (!s) return true;
            const hay = [it.voucher_no, it.trip_title, it.slug, it.customer?.name, it.customer?.phone, it.customer?.email, (it.destinations || []).join(' ')]
              .filter(Boolean).join(' ').toLowerCase();
            return hay.includes(s);
          }).map((it) => (
            <div key={it.id} data-testid={`itinerary-row-${it.id}`} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900 truncate">{it.trip_title || 'Untitled trip'}</div>
                  <div className="text-[11px] text-gray-500 truncate flex items-center gap-1.5"><MapPin size={11}/> {(it.destinations || []).join(', ') || '—'}</div>
                  <div className="text-[11px] text-gray-500 truncate flex items-center gap-1.5 mt-0.5"><Users size={11}/> {it.customer?.name || '—'} · {it.customer?.phone || ''}</div>
                  <div className="text-[11px] text-gray-500 truncate flex items-center gap-1.5"><Calendar size={11}/> {fmtDate(it.start_date) || '—'} → {fmtDate(it.end_date) || '—'} · {it.pax_adults}A {it.pax_children}C</div>
                </div>
                <div className="text-right text-[11px] text-gray-500 shrink-0">
                  <div className="font-semibold text-[#0d64a8]">INR {Math.round(Number(it.total_amount || 0)).toLocaleString('en-IN')}</div>
                  <div>{(it.days || []).length} days</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-1.5 flex-wrap">
                <button onClick={() => startEdit(it)} data-testid={`it-edit-${it.id}`} className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><FileText size={12}/> Edit</button>
                <button onClick={() => downloadPdf(it)} data-testid={`it-pdf-${it.id}`} className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><Download size={12}/> PDF</button>
                <VoucherActions
                  it={it}
                  onPreview={previewVoucher}
                  onDownload={downloadVoucher}
                  onEmail={emailVoucher}
                  onWhatsApp={shareVoucherWA}
                  onReissue={it.voucher_no ? reissueVoucher : null}
                />
                <button onClick={() => shareEmail(it)} data-testid={`it-email-${it.id}`} className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><Send size={12}/> Email</button>
                <button onClick={() => shareWA(it)} data-testid={`it-wa-${it.id}`} className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md text-[#25D366] hover:bg-[#25D366]/10 flex items-center gap-1"><MessageCircle size={12}/> WhatsApp</button>
                <button onClick={() => copyLink(it)} data-testid={`it-link-${it.id}`} className={`text-xs px-2.5 py-1.5 border rounded-md flex items-center gap-1 ${copiedFor === it.id ? 'border-green-300 text-green-700 bg-green-50' : 'border-gray-200 hover:border-[#0d64a8] hover:text-[#0d64a8]'}`}>{copiedFor === it.id ? <><Check size={12}/> Copied</> : <><Link2 size={12}/> Copy link</>}</button>
                <button onClick={() => duplicate(it)} data-testid={`it-dup-${it.id}`} className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1"><Copy size={12}/> Duplicate</button>
                <button onClick={() => remove(it)} data-testid={`it-del-${it.id}`} className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md text-gray-500 hover:border-red-400 hover:text-red-500 ml-auto flex items-center gap-1"><Trash2 size={12}/> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <VoucherPreviewModal
        it={voucherPreview}
        open={!!voucherPreview}
        onClose={() => setVoucherPreview(null)}
        onDownload={downloadVoucher}
        onEmail={emailVoucher}
        onWhatsApp={shareVoucherWA}
      />
    </div>
  );
};

export default ItineraryBuilder;
